<script setup lang="ts">
import axios from 'axios';
import {
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BModal,
} from 'bootstrap-vue-next';
import { computed, ref } from 'vue';

import type { PostApiV1TranscendHackathonError } from '~/api/generated';
import { createRegistration } from '~/api/transcendHackathon';
import ErrorState from '~/common/components/ErrorState.vue';

const DEVPOST_EVENT_URL = 'https://gitlab-transcend.devpost.com/';

const props = withDefaults(defineProps<{
  status?: string;
  groupUrl?: string;
  projectUrl?: string;
}>(), {
  status: 'none',
  groupUrl: '',
  projectUrl: '',
});

const model = defineModel<boolean>();
const emit = defineEmits<{
  registered: [{ status: string; projectUrl: string }];
}>();

const devpostUsername = ref('');
const submitting = ref(false);
const errorMessage = ref<string | null>(null);

const isApproved = computed(() => props.status === 'approved');
const isPending = computed(() => props.status === 'pending');
const isRejected = computed(() => props.status === 'rejected');

const resetForm = (): void => {
  devpostUsername.value = '';
  errorMessage.value = null;
};

const handleSubmit = async (): Promise<void> => {
  if (submitting.value) {
    return;
  }

  submitting.value = true;
  errorMessage.value = null;

  try {
    const data = await createRegistration(devpostUsername.value);

    emit('registered', {
      status: data.status,
      projectUrl: '',
    });
  } catch (error) {
    errorMessage.value = axios.isAxiosError<PostApiV1TranscendHackathonError>(error)
      ? error.response?.data.error ?? ''
      : '';
  } finally {
    submitting.value = false;
  }
};
</script>

<template>
  <BModal
    v-model="model"
    title="Register for the Transcend Hackathon"
    no-footer
    @hidden="resetForm"
  >
    <template v-if="isRejected">
      <p>
        Your registration was not approved. Please contact us via the
        <a
          href="https://gitlab.com/gitlab-org/developer-relations/contributor-success/contributors-gitlab-com/-/issues/new?issuable_template=default"
          target="_blank"
        >contributors-gitlab-com issue tracker</a> or find us on our <a
          href="https://discord.gg/gitlab"
          target="_blank"
        >Discord</a>.
      </p>
    </template>
    <template v-else-if="isApproved">
      <p>
        You are in! Your subgroup and project are ready.
      </p>
      <p v-if="projectUrl">
        Open your
        <a
          :href="projectUrl"
          target="_blank"
        >project</a> to get started, and read the onboarding issue we
        posted there.
      </p>
      <p v-if="groupUrl">
        Monitoring and incident response are part of life after code, so you
        can enable your own observability instance from
        <strong>Observe &rarr; Observability configuration &rarr; Enable
          Observability</strong> in
        <a
          :href="groupUrl"
          target="_blank"
        >your subgroup</a>. See the
        <a
          href="https://docs.gitlab.com/operations/observability/observability/"
          target="_blank"
        >observability documentation</a> for details. It is optional and not
        required to submit or to win.
      </p>
      <p v-if="!projectUrl">
        Your workspace is still being provisioned. Please check back shortly.
      </p>
    </template>
    <template v-else-if="isPending">
      <p>
        Thanks for registering! Your registration is now awaiting
        admin approval. Approvals and provisioning start on October 5,
        when submissions open. We will create a dedicated GitLab subgroup
        and project for you once your registration is reviewed.
      </p>
      <p>
        To be eligible for cash prizes you must also register on
        <a
          :href="DEVPOST_EVENT_URL"
          target="_blank"
        >Devpost</a>, and submit your final project there before
        October 27, 2026.
      </p>
    </template>
    <template v-else-if="errorMessage !== null">
      <p v-if="errorMessage">
        {{ errorMessage }}
      </p>
      <ErrorState v-else />
    </template>
    <BForm
      v-else
      @submit.prevent="handleSubmit"
    >
      <p>
        Register below so we can provision a GitLab subgroup and project for
        you once your registration is approved by an admin. Approvals and
        provisioning start on October 5, when submissions open.
      </p>
      <p>
        You must also register on
        <a
          :href="DEVPOST_EVENT_URL"
          target="_blank"
        >Devpost</a> to be eligible for cash prizes, and submit
        your final project there before October 27, 2026.
      </p>
      <BFormGroup
        label="Devpost username"
        class="mb-3"
        description="Your Devpost profile username for hackathon submission tracking."
      >
        <BFormInput
          v-model="devpostUsername"
          required
          aria-required="true"
          placeholder="your-devpost-username"
        />
      </BFormGroup>
      <BButton
        type="submit"
        class="float-end"
        variant="primary"
        :aria-busy="submitting"
      >
        Register
      </BButton>
    </BForm>
  </BModal>
</template>
